import { LoadingButton } from '@mui/lab';
import { Button, Checkbox, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, TextField, Typography } from '@mui/material';
import { Box } from '@mui/system';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { API_CALL_STATUS, COLORS_BY_STATUS, MATCH_RESPONSE_STATUS } from '../../../../../utils/constants';
import { useDebounce } from '../../../../../utils/debounce';
import timeout from '../../../../../utils/timeout';

let isCanceled = false;
const getIsCanceled = () => {
  return isCanceled;
};
const setIsCanceled = (value) => {
  isCanceled = value;
};

const ItemRow = ({ item, hash, onChange, isLoading }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Divider />
      <Box sx={{ display: "flex", alignItems: "center", width: '100%', gap: '5px' }}>
        <Checkbox checked={hash[item].checked} onChange={onChange} disabled={isLoading} />
        <Typography variant='body1' title={hash[item].name} style={{ width: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{hash[item].name}</Typography>
        <Box sx={{ minWidth: 'fit-content', display: 'flex', gap: '5px', alignItems: 'center', width: '40px' }}>
          {hash[item].status === API_CALL_STATUS.PENDING && <CircularProgress color='success' size={30} />}
        </Box>
        {hash[item].status && hash[item].status !== API_CALL_STATUS.PENDING &&
          <Typography variant='body1' style={{ color: COLORS_BY_STATUS[hash[item].status] }}>
            {hash[item].status}
          </Typography>
        }
      </Box>
      <Divider />
    </Box>
  )
};

const HeaderRow = ({ hash, checkedAmount, onChange, isChecked, isLoading, processingItemsAmount, processingItemIndex }) => {
  const checkedText = useMemo(() => {
    if (!hash) {
      return null;
    }
    return `Checked (${checkedAmount}/${Object.keys(hash).length})`;
  }, [hash, checkedAmount]);
  const processingText = useMemo(() => {
    if (!processingItemsAmount) {
      return null;
    }
    return `Processing (${processingItemIndex + 1}/${processingItemsAmount})`;
  }, [processingItemsAmount, processingItemIndex]);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', position: 'sticky', top: '0', zIndex: '1000', background: 'white' }}>
      <Divider />
      <Box sx={{ display: "flex", alignItems: "center", width: '100%', gap: '5px' }}>
        <Checkbox checked={isChecked} onChange={onChange} disabled={isLoading} />
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: 'space-between', width: '100%' }}>
          <Typography variant='body1'>{checkedText}</Typography>
          <Typography variant='body1'>{processingText}</Typography>
        </Box>
      </Box>
      <Divider />
    </Box>
  )
};

const MatchAiDialog = ({ open, items, sport, title, onClose, onChange, fetchFunc, payloadFieldName }) => {
  const [hash, setHash] = useState(null);
  const [uniqueKeys, setUniqueKeys] = useState([]);
  const [filter, setFilter] = useState('');
  const searchTerm = useDebounce(filter, 300);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckedAll, setIsCheckedAll] = useState(false);
  const [processingItemsAmount, setProcessingItemsAmount] = useState(null);
  const [processingItemIndex, setProcessingItemIndex] = useState(null);
  const [matchedItems, setMatchedItems] = useState([]);

  const setup = useCallback(() => {
    const uniqueKeys = [...new Set(items.map((item) => item.unionKey))];
    const hash = items.reduce((a, c) => {
      if (!c.unionKey) {
        return a;
      }
      if (a[c.unionKey]) {
        a[c.unionKey].name = `${a[c.unionKey].name}/${c.name}`;
      } else {
        a[c.unionKey] = {
          name: c.name,
          checked: false,
          status: null,
        };
      }
      return a;
    }, {});
    setHash(hash);
    setUniqueKeys(uniqueKeys);
  }, [setHash, items]);

  const checkedAmount = useMemo(() => {
    return Object.values(hash || {})?.filter((item) => item.checked)?.length || 0;
  }, [hash]);

  const handleChangeHash = useCallback((unionKey, args) => {
    setHash((prev) => ({
      ...prev,
      [unionKey]: {
        ...prev[unionKey],
        ...args
      }
    }));
  }, [setHash]);

  const filteredItems = useMemo(() => {
    if (!searchTerm || !hash) {
      return uniqueKeys;
    }
    const filteredItems = uniqueKeys.filter((item) => hash[item].name.toLowerCase().includes(searchTerm.toLowerCase()));
    return filteredItems;
  }, [uniqueKeys, hash, searchTerm]);

  const handleChangeAll = useCallback((e) => {
    const checked = e.target.checked;
    const newHash = Object.keys(hash).reduce((a, c) => {
      a[c] = { ...hash[c], checked };
      return a;
    }, {});
    setHash(newHash);
    setIsCheckedAll(checked);
  }, [hash]);

  const handleChangeFilter = useCallback((e) => {
    setFilter(e.target.value);
  }, []);

  const handleChange = useCallback((unionKey) => {
    handleChangeHash(unionKey, { checked: !hash[unionKey].checked, status: null });
  }, [handleChangeHash, hash]);

  const handleAiMatch = useCallback(async (unionKey) => {
    if (getIsCanceled()) {
      handleChangeHash(unionKey, { status: API_CALL_STATUS.CANCELED });
      return [];
    }
    handleChangeHash(unionKey, { status: API_CALL_STATUS.PENDING });
    const { status, matches } = await fetchFunc(sport, { [payloadFieldName]: unionKey });

    if (status === MATCH_RESPONSE_STATUS.INPROGRESS) {
      await timeout(5000);
      return handleAiMatch(unionKey);
    }

    if (status === MATCH_RESPONSE_STATUS.FAILED) {
      handleChangeHash(unionKey, { status: API_CALL_STATUS.FAILED });
    }
    handleChangeHash(unionKey, { status: API_CALL_STATUS.SUCCESS, checked: false });
    return matches;
  }, [sport, handleChangeHash, fetchFunc, payloadFieldName]);

  const handleStartAiMatch = useCallback(async () => {
    const checkedItems = uniqueKeys.filter((item) => hash[item].checked);
    setIsLoading(true);
    let matchedItems = [];
    for (const [index, unionKey] of checkedItems.entries()) {
      try {
        setProcessingItemIndex(index);
        setProcessingItemsAmount(checkedItems.length);
        const matches = await handleAiMatch(unionKey);
        matchedItems = matchedItems.concat(matches);
      } catch (e) {
        console.log(e);
        toast.error('Servers are busy. Try again later!');
      } finally {
      }
    }
    if (!getIsCanceled()) {
      return onChange(matchedItems);
    }
    setMatchedItems(matchedItems);
    setProcessingItemIndex(null);
    setProcessingItemsAmount(null);
    setIsLoading(false);
    setIsCanceled(false);
  }, [uniqueKeys, hash, handleAiMatch, onChange]);

  useEffect(() => {
    setup();
  }, [setup]);

  useEffect(() => {
    if (checkedAmount === Object.keys(hash || {}).length) {
      return setIsCheckedAll(true);
    }
    setIsCheckedAll(false);
  }, [checkedAmount, hash]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='lg'>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ maxHeight: 'calc(100vh - 350px)', overflow: 'auto' }}>
          <HeaderRow hash={hash} checkedAmount={checkedAmount} isChecked={isCheckedAll} onChange={handleChangeAll} isLoading={isLoading} processingItemsAmount={processingItemsAmount} processingItemIndex={processingItemIndex} />
          {hash && filteredItems.map((item) =>
            <ItemRow key={item} item={item} hash={hash} onChange={() => handleChange(item)} isLoading={isLoading} />
          )}
        </Box>
      </DialogContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 24px 20px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {!isLoading && <LoadingButton
            variant="contained"
            color={"info"}
            onClick={handleStartAiMatch}
            loading={isLoading}
          >
            Start
          </LoadingButton>}
          {isLoading && <LoadingButton
            variant="contained"
            color={"error"}
            onClick={() => setIsCanceled(true)}
            loading={isCanceled}
          >
            Stop
          </LoadingButton>}
          <TextField
            name="text"
            variant="outlined"
            size="small"
            label="Filter"
            value={filter}
            onChange={handleChangeFilter}
          />
        </Box>
        {!matchedItems.length && <Button
          variant="contained"
          color={"error"}
          onClick={onClose}
        >
          Close
        </Button>}
        {!!matchedItems.length && <Button
          variant="contained"
          color={"success"}
          onClick={() => onChange(matchedItems)}
        >
          Apply
        </Button>}
      </Box>
    </Dialog>
  );
}

export default MatchAiDialog;
